import { ArrowTopRightIcon, Crosshair2Icon, CubeIcon, DoubleArrowRightIcon, EnvelopeOpenIcon, GlobeIcon, InfoCircledIcon, InputIcon, MixIcon, Pencil1Icon, RocketIcon, UpdateIcon } from '@radix-ui/react-icons';
import { Box, Button, Card, FeatureIcon, Flex, Grid, Heading, Text } from '@workos-inc/design-system';
import Head from 'next/head';
import NextLink from 'next/link';
import { Header } from 'src/components/header';
import { NavigationWrapper } from 'src/components/navigation-wrapper';
import { PageBackground } from 'src/components/page-background';
import { PageLayout } from 'src/components/page-layout';
import { ApiReferenceIcon } from 'src/icons/api-reference-icon';
import { ApplicationsIcon } from 'src/icons/applications-icon';
import { EventsIcon } from 'src/icons/events-icon';
import { MetadataIcon } from 'src/icons/metadata-logo-icon';
import { RadarIcon } from 'src/icons/radar-icon';
import { SsoIcon } from 'src/icons/sso-icon';
import { ThickArrowRightIcon } from 'src/icons/thick-arrow-right-icon';
import { UserIdCardsIcon } from 'src/icons/user-id-cards-icon';
import { createPageDescription, createPageTitle } from 'src/utils/create-page-metadata';
const title = createPageTitle();
const description = createPageDescription();
export const image = 'https://images.workoscdn.com/images/eb8e2f4a-3f05-4834-9184-f06c65281dfc.png?auto=format&fit=clip&q=95';
const authenticationItems = [{
  title: 'Hosted UI',
  desc: 'Customizable hosted UI for authentication at any size.',
  icon: InputIcon,
  url: '/authkit/hosted-ui'
}, {
  title: 'Enterprise auth',
  desc: 'Single sign-on, passkeys, social sign-in, passwords, and more.',
  icon: SsoIcon,
  url: '/authkit/sso'
}, {
  title: 'Roles and Permissions',
  desc: 'Advanced RBAC with custom roles and role assignment.',
  icon: UserIdCardsIcon,
  url: '/authkit/roles-and-permissions'
}, {
  title: 'Radar',
  desc: 'Protect your app from bots, fraud, and abuse.',
  icon: RadarIcon,
  url: '/authkit/radar'
}, {
  title: 'Custom Metadata',
  desc: 'Store additional information about users and organizations.',
  icon: MetadataIcon,
  url: '/authkit/metadata'
}, {
  title: 'Connect',
  desc: 'Built-in support for MCP and OAuth applications.',
  icon: ApplicationsIcon,
  url: '/authkit/connect'
}] as const;
const eventsItems = [{
  title: 'Event types',
  desc: 'Respond to activity that occurs within WorkOS and third-party providers.',
  icon: EventsIcon,
  url: '/events'
}, {
  title: 'Data syncing',
  desc: 'Learn to keep your app in sync using the events API and webhooks.',
  icon: UpdateIcon,
  url: '/events/data-syncing'
}, {
  title: 'Observability',
  desc: 'Stream and analyze WorkOS activity in Datadog.',
  icon: Crosshair2Icon,
  url: '/events/observability/datadog'
}];
const resourceItems = [{
  title: 'API reference',
  desc: 'Every SDK method and API endpoint documented.',
  icon: ApiReferenceIcon,
  url: '/reference'
}, {
  title: 'Integrations',
  desc: 'Guides on integrating WorkOS with third‑party services.',
  icon: MixIcon,
  url: '/integrations'
}, {
  title: 'Migrate to WorkOS',
  desc: 'Power your existing authentication stack with WorkOS features.',
  icon: DoubleArrowRightIcon,
  url: '/migrate'
}, {
  title: 'SDKs',
  desc: 'Official open-source SDK repositories and changelogs.',
  icon: CubeIcon,
  url: '/sdks'
}, {
  title: 'Email delivery',
  desc: 'Best practices for sending email with WorkOS.',
  icon: EnvelopeOpenIcon,
  url: '/email'
}, {
  title: 'Custom domains',
  desc: 'Set up custom domains for your integration.',
  icon: GlobeIcon,
  url: '/custom-domains'
}, {
  title: 'Glossary',
  desc: 'All the terminology and concepts used in the docs.',
  icon: InfoCircledIcon,
  url: '/glossary'
}, {
  title: 'Changelog',
  desc: 'Updates on what we are shipping in the product every week.',
  icon: RocketIcon,
  url: 'https://workos.com/changelog'
}, {
  title: 'Blog',
  desc: 'Engineering and product insights from the team at WorkOS.',
  icon: Pencil1Icon,
  url: 'https://workos.com/blog'
}];
const HomePage = () => <Box position="relative" data-sentry-element="Box" data-sentry-component="HomePage" data-sentry-source-file="index.tsx">
    <Head data-sentry-element="Head" data-sentry-source-file="index.tsx">
      <title>{title}</title>
      <meta content={description} name="description" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
      <meta content={image} itemProp="image" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
      <meta content={image} name="image" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
      <meta content={description} name="og:description" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
      <meta content={image} property="og:image" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
      <meta content={title} property="og:title" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
      <meta content="website" property="og:type" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
      <meta content="summary_large_image" name="twitter:card" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
      <meta content={description} name="twitter:description" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
      <meta content={image} name="twitter:image" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
      <meta content={title} name="twitter:title" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
    </Head>

    <PageBackground data-sentry-element="PageBackground" data-sentry-source-file="index.tsx" />

    <Header data-sentry-element="Header" data-sentry-source-file="index.tsx" />

    <PageLayout.Root layout="index" data-sentry-element="PageLayout.Root" data-sentry-source-file="index.tsx">
      <PageLayout.Nav data-sentry-element="PageLayout.Nav" data-sentry-source-file="index.tsx">
        <NavigationWrapper data-sentry-element="NavigationWrapper" data-sentry-source-file="index.tsx" />
      </PageLayout.Nav>

      <PageLayout.Content data-sentry-element="PageLayout.Content" data-sentry-source-file="index.tsx">
        <Flex direction="column" gap="9" mb="7" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
          <Grid asChild gap={{
          initial: '3',
          sm: '5'
        }} mb="5" columns={{
          initial: '1fr',
          md: 'repeat(6, 1fr)'
        }} data-sentry-element="Grid" data-sentry-source-file="index.tsx">
            <section>
              <Box gridColumn={{
              initial: '1 / 1',
              md: '1 / 3'
            }} data-sentry-element="Box" data-sentry-source-file="index.tsx">
                <FeatureIcon feature="user-management" mb="5" data-sentry-element="FeatureIcon" data-sentry-source-file="index.tsx" />

                <Heading as="h1" mb="2" size={{
                initial: '6',
                sm: '7'
              }} data-sentry-element="Heading" data-sentry-source-file="index.tsx">
                  AuthKit
                </Heading>
                <Text as="p" color="gray" mb="5" size="3" data-sentry-element="Text" data-sentry-source-file="index.tsx">
                  Full-fledged authentication platform, from your first user to
                  enterprise{' '}
                  <span style={{
                  whiteSpace: 'nowrap'
                }}>single sign-on</span>.
                </Text>

                <Button asChild color="purple" size="3" data-sentry-element="Button" data-sentry-source-file="index.tsx">
                  <NextLink href="/authkit" data-sentry-element="NextLink" data-sentry-source-file="index.tsx">
                    Go to Quick Start
                    <ThickArrowRightIcon style={{
                    opacity: 1,
                    marginTop: 1,
                    marginRight: -2,
                    marginLeft: -2
                  }} data-sentry-element="ThickArrowRightIcon" data-sentry-source-file="index.tsx" />
                  </NextLink>
                </Button>
              </Box>

              <Box gridColumn={{
              initial: '1 / 1',
              md: '3 / 7'
            }} mt={{
              initial: '5',
              md: '0'
            }} data-sentry-element="Box" data-sentry-source-file="index.tsx">
                <Grid columns={{
                initial: '1',
                sm: '2',
                md: '3'
              }} gap={{
                initial: '2',
                sm: '3'
              }} data-sentry-element="Grid" data-sentry-source-file="index.tsx">
                  {authenticationItems.map(({
                  icon: Icon,
                  ...item
                }) => <Card key={item.title} asChild size="3" variant="classic">
                      <NextLink href={item.url}>
                        <Box mb="4">
                          <Icon height="32" width="32" />
                        </Box>
                        <Heading as="h3" mb="1" size="3" trim="start">
                          {item.title}
                        </Heading>
                        <Text as="p" size="3">
                          {item.desc}
                        </Text>
                      </NextLink>
                    </Card>)}
                </Grid>
              </Box>
            </section>
          </Grid>

          <section>
            <Heading as="h1" mb={{
            initial: '4',
            sm: '5'
          }} size={{
            initial: '6',
            sm: '7'
          }} data-sentry-element="Heading" data-sentry-source-file="index.tsx">
              Additional Products
            </Heading>

            <Grid columns={{
            initial: '1',
            sm: '2',
            md: '3'
          }} gap={{
            initial: '3',
            sm: '5'
          }} data-sentry-element="Grid" data-sentry-source-file="index.tsx">
              <Card asChild size="3" variant="classic" data-sentry-element="Card" data-sentry-source-file="index.tsx">
                <NextLink href="/sso" data-sentry-element="NextLink" data-sentry-source-file="index.tsx">
                  <Flex direction="column" height="100%" justify="start" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
                    <FeatureIcon feature="sso" mb="5" data-sentry-element="FeatureIcon" data-sentry-source-file="index.tsx" />
                    <Box data-sentry-element="Box" data-sentry-source-file="index.tsx">
                      <Heading as="h2" mb="1" size="4" data-sentry-element="Heading" data-sentry-source-file="index.tsx">
                        Single Sign-On
                      </Heading>
                      <Text as="p" data-sentry-element="Text" data-sentry-source-file="index.tsx">
                        Integrate your existing auth stack with any SAML or OIDC
                        identity provider.
                      </Text>
                    </Box>
                  </Flex>
                </NextLink>
              </Card>

              <Card asChild size="3" variant="classic" data-sentry-element="Card" data-sentry-source-file="index.tsx">
                <NextLink href="/directory-sync" data-sentry-element="NextLink" data-sentry-source-file="index.tsx">
                  <Flex direction="column" height="100%" justify="start" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
                    <FeatureIcon feature="directory-sync" mb="5" data-sentry-element="FeatureIcon" data-sentry-source-file="index.tsx" />
                    <Box data-sentry-element="Box" data-sentry-source-file="index.tsx">
                      <Heading as="h2" mb="1" size="4" data-sentry-element="Heading" data-sentry-source-file="index.tsx">
                        Directory Sync
                      </Heading>
                      <Text as="p" data-sentry-element="Text" data-sentry-source-file="index.tsx">
                        Add user lifecycle management for SCIM and HRIS
                        providers to your existing stack.
                      </Text>
                    </Box>
                  </Flex>
                </NextLink>
              </Card>

              <Card asChild size="3" variant="classic" data-sentry-element="Card" data-sentry-source-file="index.tsx">
                <NextLink href="/admin-portal" data-sentry-element="NextLink" data-sentry-source-file="index.tsx">
                  <Flex direction="column" height="100%" justify="start" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
                    <FeatureIcon feature="admin-portal" mb="5" data-sentry-element="FeatureIcon" data-sentry-source-file="index.tsx" />
                    <Box data-sentry-element="Box" data-sentry-source-file="index.tsx">
                      <Heading as="h2" mb="1" size="4" data-sentry-element="Heading" data-sentry-source-file="index.tsx">
                        Admin Portal
                      </Heading>
                      <Text as="p" data-sentry-element="Text" data-sentry-source-file="index.tsx">
                        Enable IT admins to have a self-serve onboarding
                        experience.
                      </Text>
                    </Box>
                  </Flex>
                </NextLink>
              </Card>

              <Card asChild size="3" variant="classic" data-sentry-element="Card" data-sentry-source-file="index.tsx">
                <NextLink href="/audit-logs" data-sentry-element="NextLink" data-sentry-source-file="index.tsx">
                  <Flex direction="column" height="100%" justify="start" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
                    <FeatureIcon feature="audit-logs" mb="5" data-sentry-element="FeatureIcon" data-sentry-source-file="index.tsx" />
                    <Box position="relative" data-sentry-element="Box" data-sentry-source-file="index.tsx">
                      <Heading as="h2" mb="1" size="4" data-sentry-element="Heading" data-sentry-source-file="index.tsx">
                        Audit Logs
                      </Heading>
                      <Text as="p" data-sentry-element="Text" data-sentry-source-file="index.tsx">
                        Ingest and export audit log events from your
                        applications.
                      </Text>
                    </Box>
                  </Flex>
                </NextLink>
              </Card>

              <Card asChild size="3" variant="classic" data-sentry-element="Card" data-sentry-source-file="index.tsx">
                <NextLink href="/rbac" data-sentry-element="NextLink" data-sentry-source-file="index.tsx">
                  <Flex direction="column" height="100%" justify="start" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
                    <FeatureIcon feature="rbac" mb="5" data-sentry-element="FeatureIcon" data-sentry-source-file="index.tsx" />
                    <Box data-sentry-element="Box" data-sentry-source-file="index.tsx">
                      <Heading as="h2" mb="1" size="4" data-sentry-element="Heading" data-sentry-source-file="index.tsx">
                        Role-Based Access Control
                      </Heading>
                      <Text as="p" data-sentry-element="Text" data-sentry-source-file="index.tsx">
                        Manage user access using flexible roles and permissions.
                      </Text>
                    </Box>
                  </Flex>
                </NextLink>
              </Card>

              <Card asChild size="3" variant="classic" data-sentry-element="Card" data-sentry-source-file="index.tsx">
                <NextLink href="/fga" data-sentry-element="NextLink" data-sentry-source-file="index.tsx">
                  <Flex direction="column" height="100%" justify="start" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
                    <FeatureIcon feature="fga" mb="5" data-sentry-element="FeatureIcon" data-sentry-source-file="index.tsx" />
                    <Box position="relative" data-sentry-element="Box" data-sentry-source-file="index.tsx">
                      <Heading as="h2" mb="1" size="4" data-sentry-element="Heading" data-sentry-source-file="index.tsx">
                        Fine-Grained Authorization
                      </Heading>
                      <Text as="p" data-sentry-element="Text" data-sentry-source-file="index.tsx">
                        Implement fine grained authorization in your
                        application.
                      </Text>
                    </Box>
                  </Flex>
                </NextLink>
              </Card>

              <Card asChild size="3" variant="classic" data-sentry-element="Card" data-sentry-source-file="index.tsx">
                <NextLink href="/vault" data-sentry-element="NextLink" data-sentry-source-file="index.tsx">
                  <Flex direction="column" height="100%" justify="start" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
                    <FeatureIcon feature="vault" mb="5" data-sentry-element="FeatureIcon" data-sentry-source-file="index.tsx" />
                    <Box position="relative" data-sentry-element="Box" data-sentry-source-file="index.tsx">
                      <Heading as="h2" mb="1" size="4" data-sentry-element="Heading" data-sentry-source-file="index.tsx">
                        Vault
                      </Heading>
                      <Text as="p" data-sentry-element="Text" data-sentry-source-file="index.tsx">
                        Encrypt, store, and control access to sensitive data.
                      </Text>
                    </Box>
                  </Flex>
                </NextLink>
              </Card>

              <Card asChild size="3" variant="classic" data-sentry-element="Card" data-sentry-source-file="index.tsx">
                <NextLink href="/pipes" data-sentry-element="NextLink" data-sentry-source-file="index.tsx">
                  <Flex direction="column" height="100%" justify="start" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
                    <FeatureIcon feature="pipes" mb="5" data-sentry-element="FeatureIcon" data-sentry-source-file="index.tsx" />
                    <Box position="relative" data-sentry-element="Box" data-sentry-source-file="index.tsx">
                      <Heading as="h2" mb="1" size="4" data-sentry-element="Heading" data-sentry-source-file="index.tsx">
                        Pipes
                      </Heading>
                      <Text as="p" data-sentry-element="Text" data-sentry-source-file="index.tsx">
                        Enable your customers to connect their third-party
                        accounts to your application.
                      </Text>
                    </Box>
                  </Flex>
                </NextLink>
              </Card>

              <Card asChild size="3" variant="classic" data-sentry-element="Card" data-sentry-source-file="index.tsx">
                <NextLink href="/feature-flags" data-sentry-element="NextLink" data-sentry-source-file="index.tsx">
                  <Flex direction="column" height="100%" justify="start" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
                    <FeatureIcon feature="feature-flags" mb="5" data-sentry-element="FeatureIcon" data-sentry-source-file="index.tsx" />
                    <Box position="relative" data-sentry-element="Box" data-sentry-source-file="index.tsx">
                      <Heading as="h2" mb="1" size="4" data-sentry-element="Heading" data-sentry-source-file="index.tsx">
                        Feature Flags
                      </Heading>
                      <Text as="p" data-sentry-element="Text" data-sentry-source-file="index.tsx">
                        Control rollout of new features in your application.
                      </Text>
                    </Box>
                  </Flex>
                </NextLink>
              </Card>

              <Card asChild size="3" variant="classic" data-sentry-element="Card" data-sentry-source-file="index.tsx">
                <NextLink href="/widgets" data-sentry-element="NextLink" data-sentry-source-file="index.tsx">
                  <Flex direction="column" height="100%" justify="start" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
                    <FeatureIcon feature="widgets" mb="5" data-sentry-element="FeatureIcon" data-sentry-source-file="index.tsx" />
                    <Box position="relative" data-sentry-element="Box" data-sentry-source-file="index.tsx">
                      <Heading as="h2" mb="1" size="4" data-sentry-element="Heading" data-sentry-source-file="index.tsx">
                        Widgets
                      </Heading>
                      <Text as="p" data-sentry-element="Text" data-sentry-source-file="index.tsx">
                        Complete functionality for common enterprise app
                        workflows.
                      </Text>
                    </Box>
                  </Flex>
                </NextLink>
              </Card>

              <Card asChild size="3" variant="classic" data-sentry-element="Card" data-sentry-source-file="index.tsx">
                <NextLink href="/domain-verification" data-sentry-element="NextLink" data-sentry-source-file="index.tsx">
                  <Flex direction="column" height="100%" justify="start" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
                    <FeatureIcon feature="domain-verification" mb="5" data-sentry-element="FeatureIcon" data-sentry-source-file="index.tsx" />
                    <Box position="relative" data-sentry-element="Box" data-sentry-source-file="index.tsx">
                      <Heading as="h2" mb="1" size="4" data-sentry-element="Heading" data-sentry-source-file="index.tsx">
                        Domain Verification
                      </Heading>
                      <Text as="p" data-sentry-element="Text" data-sentry-source-file="index.tsx">
                        Allow your customers to securely claim ownership of a
                        domain.
                      </Text>
                    </Box>
                  </Flex>
                </NextLink>
              </Card>
            </Grid>
          </section>

          <section>
            <Heading as="h1" mb={{
            initial: '4',
            sm: '5'
          }} size={{
            initial: '6',
            sm: '7'
          }} data-sentry-element="Heading" data-sentry-source-file="index.tsx">
              Events and webhooks
            </Heading>

            <Grid columns={{
            initial: '1',
            sm: '2',
            md: '3'
          }} gap={{
            initial: '3',
            sm: '5'
          }} data-sentry-element="Grid" data-sentry-source-file="index.tsx">
              {eventsItems.map(({
              icon: Icon,
              ...item
            }) => <Card key={item.url} asChild size="3" variant="classic">
                  <NextLink href={item.url}>
                    <Box mb="4">
                      <Icon height="32" width="32" />
                    </Box>
                    <Heading as="h2" mb={{
                  sm: '1'
                }} size="4">
                      <Flex align="center" gap="2">
                        {item.title}
                      </Flex>
                    </Heading>

                    <Text as="p" size="3">
                      {item.desc}
                    </Text>

                    {item.url.startsWith('http') && <Box m="3" position="absolute" right="0" top="0">
                        <ArrowTopRightIcon height="24" style={{
                    color: 'var(--gray-a8)'
                  }} width="24" />
                      </Box>}
                  </NextLink>
                </Card>)}
            </Grid>
          </section>

          <section>
            <Heading as="h1" mb={{
            initial: '4',
            sm: '5'
          }} size={{
            initial: '6',
            sm: '7'
          }} data-sentry-element="Heading" data-sentry-source-file="index.tsx">
              Resources
            </Heading>

            <Grid columns={{
            initial: '1',
            sm: '2',
            md: '3'
          }} gap={{
            initial: '3',
            sm: '5'
          }} data-sentry-element="Grid" data-sentry-source-file="index.tsx">
              {resourceItems.map(({
              icon: Icon,
              ...item
            }) => <Card key={item.url} asChild size="3" variant="classic">
                  <NextLink href={item.url}>
                    <Box mb="4">
                      <Icon height="32" width="32" />
                    </Box>
                    <Heading as="h2" mb={{
                  sm: '1'
                }} size="4">
                      <Flex align="center" gap="2">
                        {item.title}
                      </Flex>
                    </Heading>

                    <Text as="p" size="3">
                      {item.desc}
                    </Text>

                    {item.url.startsWith('http') && <Box m="3" position="absolute" right="0" top="0">
                        <ArrowTopRightIcon height="24" style={{
                    color: 'var(--gray-a8)'
                  }} width="24" />
                      </Box>}
                  </NextLink>
                </Card>)}
            </Grid>
          </section>
        </Flex>
      </PageLayout.Content>
    </PageLayout.Root>
  </Box>;
export default HomePage;